import { DataTable, Card, CardContent, PlusButton } from '@ucc/react/ui';
import { Projekt, Subprojekt } from '~/gql/ucpw/graphql';
import { useOperationalReports } from '~/pages/projects/hooks/useOperationalReports';
import { ProjectIds } from '~/pages/projects/types';
import { useEditable } from '~/pages/projects/hooks/useEditable';
import { useModal, useViewer } from '~/hooks';
import { combineReducers, modalReducer, rowReducer } from '~/reducers';
import { isPxConnectSource, serviceRequestIdFromMetadata } from '~/meta/data/px-connect';
import { HasPermission } from '~/layout/HasPermission';
import { OverrideActions } from '~/components/Layout/PageHeader';
import { MultiModal } from '~/components/MultiModal';
import { PxConnectModal } from './PxConnect';
import log from '~/log';

type Props = ProjectIds & { project?: Projekt; subproject?: Subprojekt };

export const OperationalReportList = ({ projectId, subprojectId, project, subproject }: Props) => {
    const { updateEditable } = useEditable();

    const isLeckortung = subproject?.projekttyp?.name === 'Leckortung';
    // The PX Connect Fragenkatalog only applies to orders that originated from PX
    // Connect. `sourceSystem` and `serviceRequestId` are written into the
    // subproject metadata by the approval flow (ucpw-services); subprojects
    // without them (legacy or manually created) fall back to "hide". The
    // serviceRequestId keys the catalog query (the backend derives the insurer).
    const isPxConnect = isPxConnectSource(subproject?.metadata);
    const serviceRequestId = serviceRequestIdFromMetadata(subproject?.metadata);
    // ADR 0005: der Schadenbericht-Trigger ist rollenbasiert gegatet (Gruppe "PX Connect").
    const viewer = useViewer();
    const canCreateSchadenbericht = viewer.hasPermission('pxconnect.schadenbericht', 'create');
    // Schadensobjekt = project address; Schaden-Nr lives on the subproject.
    const schadensobjekt = [
        project?.strasse,
        [project?.plz, project?.ort].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(', ');
    const schadenNr = subproject?.schadenNr ?? undefined;

    const { dispatch, onOpen } = useModal({
        defaultState: { modals: { activeModal: 'PxConnect' } },
        component: (
            <MultiModal>
                <PxConnectModal
                    id="PxConnect"
                    projectId={projectId}
                    subprojectId={subprojectId}
                    serviceRequestId={serviceRequestId}
                    schadensobjekt={schadensobjekt}
                    schadenNr={schadenNr}
                    subprojectMetadata={subproject?.metadata}
                />
            </MultiModal>
        ),
        reducer: combineReducers({
            modals: modalReducer('PxConnect'),
            row: rowReducer,
        }),
        dependencies: [serviceRequestId, schadensobjekt, schadenNr, subproject?.metadata],
    });

    const onOpenPxConnect = (report: any) => {
        dispatch?.({
            type: 'setModal',
            // scrollBehavior 'inside' keeps header/footer fixed and scrolls the
            // body, so revealed follow-up questions don't push the footer off-screen.
            data: {
                modal: 'PxConnect',
                props: { report, scrollBehavior: 'inside' },
            },
        });
        onOpen?.();
    };

    const reports = useOperationalReports({
        projectId,
        subprojectId,
        showPxConnect:
            isLeckortung && isPxConnect && Boolean(serviceRequestId) && canCreateSchadenbericht,
        onOpenPxConnect,
    });
    log.debug('OperationalReportList', reports);

    return (
        <>
            <OverrideActions>
                <HasPermission resource="project.report" permission="create">
                    <PlusButton onClick={() => updateEditable('new')}>Neuer Bericht</PlusButton>
                </HasPermission>
            </OverrideActions>
            <Card boxShadow="none">
                <CardContent>
                    <DataTable
                        columns={reports.columns}
                        hiddenColumns={reports.hiddenColumns}
                        data={reports.data}
                        loading={reports.isReadLoading}
                    />
                </CardContent>
            </Card>
        </>
    );
};
